import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { getApiError } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Plus, Search, Filter, MoreVertical, Archive, ArchiveRestore, Edit, Users, Calendar, PauseCircle, PlayCircle, CheckCircle2, Trash2, Layers, X, LayoutGrid, LayoutList, DollarSign, Sparkles, FolderKanban, Upload, Download, Loader2, BarChart3 } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { toast } from 'sonner';

const hhmmToDecimal = (str) => {
  if (!str || str.trim() === '') return 0;
  if (str.includes(':')) {
    const [hPart, mPart] = str.split(':');
    const h = parseInt(hPart, 10) || 0;
    const m = parseInt(mPart, 10) || 0;
    return Math.round((h + m / 60) * 100) / 100;
  }
  return parseFloat(str) || 0;
};

const decimalToHHMM = (decimal) => {
  if (!decimal || decimal <= 0) return '';
  const totalMinutes = Math.round(decimal * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const ServiceHoursInput = ({ value, onChange, className }) => {
  const [inputValue, setInputValue] = useState(decimalToHHMM(value) || '');
  useEffect(() => { setInputValue(decimalToHHMM(value) || ''); }, [value]);
  return (
    <Input
      type="text"
      value={inputValue}
      onChange={(e) => { if (/^\d*:?\d*$/.test(e.target.value) || e.target.value === '') setInputValue(e.target.value); }}
      onBlur={() => onChange(hhmmToDecimal(inputValue))}
      placeholder="0:00"
      className={className}
    />
  );
};

const ProjectsPage = () => {
  const { api, user, isAdmin, isSuperAdmin } = useAuth();
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [verticals, setVerticals] = useState([]);
  const [workTypes, setWorkTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('name_asc');
  const [viewMode, setViewMode] = useState('list');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editProject, setEditProject] = useState(null);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [teamMembersProject, setTeamMembersProject] = useState(null);
  const [importing, setImporting] = useState(false);
  const [expandedServiceCards, setExpandedServiceCards] = useState(new Set());
  const [monthEntries, setMonthEntries] = useState([]);
  const importFileRef = useRef(null);

  const downloadProjectTemplate = () => {
    const rows = [
      ['Name', 'Client', 'Department', 'Project Type', 'Start Date', 'End Date', 'Budgeted Hours', 'Billing Rate', 'Is Billable', 'Priority', 'Description'],
      ['App Redesign', 'Acme Corp', 'design', 'fixed_price', '2026-08-01', '2026-10-31', 120, 150, 'Yes', 'high', 'Full redesign'],
      ['SEO Campaign', 'Beta Ltd', 'marketing', 'monthly_retainer', '2026-08-01', '2026-12-31', 40, 100, 'Yes', 'medium', ''],
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'projects_template.csv';
    document.body.appendChild(a); a.click();
    document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const handleProjectImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    const toastId = toast.loading('Reading file…');
    try {
      const text = await file.text();
      const rawRows = text.split(/\r?\n/).filter(l => l.trim()).map(line => {
        const fields = []; let i = 0;
        while (i <= line.length) {
          if (line[i] === '"') {
            let j = i + 1; let field = '';
            while (j < line.length) {
              if (line[j] === '"' && line[j + 1] === '"') { field += '"'; j += 2; }
              else if (line[j] === '"') { j++; break; }
              else field += line[j++];
            }
            fields.push(field); i = j; if (line[i] === ',') i++;
          } else {
            const j = line.indexOf(',', i);
            if (j === -1) { fields.push(line.slice(i).trim()); break; }
            fields.push(line.slice(i, j).trim()); i = j + 1;
          }
        }
        return fields;
      });
      if (rawRows.length < 2) { toast.error('No data found in file', { id: toastId }); setImporting(false); return; }
      const headers = rawRows[0];
      const rows = rawRows.slice(1).map(vals => Object.fromEntries(headers.map((h, i) => [h, vals[i] ?? ''])));
      if (!rows.length) { toast.error('No data found in file', { id: toastId }); setImporting(false); return; }

      const TYPE_MAP = { 'fixed_price': 'fixed_price', 'fixed': 'fixed_price', 'monthly_retainer': 'monthly_retainer', 'retainer': 'monthly_retainer', 'time_and_materials': 'time_and_materials', 't&m': 'time_and_materials', 'non_billable': 'non_billable' };
      let created = 0, skipped = 0;
      toast.loading(`Importing 0 / ${rows.length}…`, { id: toastId });

      for (let i = 0; i < rows.length; i++) {
        const r = rows[i];
        const name = String(r['Name'] || r['name'] || '').trim();
        if (!name) { skipped++; continue; }
        const projectType = TYPE_MAP[(String(r['Project Type'] || r['project_type'] || 'fixed_price')).toLowerCase().trim()] || 'fixed_price';
        const isBillable = String(r['Is Billable'] || r['is_billable'] || 'yes').toLowerCase() !== 'no';
        const priority = ['low','medium','high','critical'].includes(String(r['Priority'] || '').toLowerCase()) ? String(r['Priority']).toLowerCase() : 'medium';
        try {
          await api.post('/projects', {
            name,
            client_name: String(r['Client'] || r['client_name'] || '').trim(),
            department: String(r['Department'] || r['department'] || '').trim(),
            project_type: projectType,
            start_date: String(r['Start Date'] || r['start_date'] || new Date().toISOString().split('T')[0]).trim(),
            end_date: String(r['End Date'] || r['end_date'] || '').trim(),
            budgeted_hours: parseFloat(r['Budgeted Hours'] || r['budgeted_hours'] || 0) || 0,
            billing_rate: parseFloat(r['Billing Rate'] || r['billing_rate'] || 0) || 0,
            is_billable: isBillable,
            priority,
            description: String(r['Description'] || r['description'] || '').trim(),
            allocations: [],
            services: [],
          });
          created++;
        } catch { skipped++; }
        toast.loading(`Importing ${created} / ${rows.length}…`, { id: toastId });
      }
      toast.success(`${created} project${created !== 1 ? 's' : ''} imported${skipped ? `, ${skipped} skipped` : ''}`, { id: toastId });
      fetchProjects();
    } catch (err) {
      toast.error('Failed to read file', { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    client_name: '',
    department: '',
    budgeted_hours: 0,
    monthly_budget_hours: null,
    project_type: 'fixed_price',
    start_date: new Date().toISOString().split('T')[0],
    end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    is_billable: true,
    priority: 'medium',
    billing_rate: 0,
    cost_rate: 0,
    description: '',
    allocations: [],
    services: [],
  });

  useEffect(() => {
    fetchProjects();
    fetchUsers();
    fetchCatalog();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = async () => {
    try {
      const now = new Date();
      const monthStart = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
      const monthEnd = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate()).padStart(2, '0')}`;
      const [res, entriesRes] = await Promise.all([
        api.get('/projects'),
        api.get(`/time-entries?date_from=${monthStart}&date_to=${monthEnd}`),
      ]);
      let projectList = res.data;
      if (!isAdmin && !isSuperAdmin) {
        projectList = projectList.filter(p =>
          p.allocations?.some(a => a.user_id === user?.id)
        );
      }
      setProjects(projectList);
      setMonthEntries(entriesRes.data || []);
    } catch (err) {
      toast.error('Failed to fetch projects');
    } finally {
      setLoading(false);
    }
  };

  const getHoursUsed = (project, forUserId = null) => {
    if (project.project_type === 'monthly_retainer') {
      return Math.round(
        monthEntries
          .filter(e => e.project_id === project.id && (!forUserId || e.user_id === forUserId))
          .reduce((s, e) => s + (e.hours || 0), 0) * 10
      ) / 10;
    }
    if (forUserId) return Math.round((project.my_logged_hours || 0) * 10) / 10;
    return Math.round((project.total_logged_hours || 0) * 10) / 10;
  };

  const getBudget = (project, userAlloc = null) => {
    if (userAlloc) return userAlloc.allocated_hours || 0;
    if (project.project_type === 'monthly_retainer') return project.monthly_budget_hours || project.budgeted_hours || 0;
    return project.budgeted_hours || 0;
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users?is_active=true');
      setUsers(res.data);
    } catch (err) {
      console.error('Failed to fetch users');
    }
  };

  const fetchCatalog = async () => {
    try {
      const [vRes, wRes] = await Promise.all([
        api.get('/service-catalog/verticals'),
        api.get('/service-catalog/work-types'),
      ]);
      setVerticals(vRes.data);
      setWorkTypes(wRes.data);
    } catch (err) {
      console.error('Failed to fetch service catalog');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      client_name: '',
      department: '',
      budgeted_hours: 0,
      monthly_budget_hours: null,
      project_type: 'fixed_price',
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      is_billable: true,
      priority: 'medium',
      billing_rate: 0,
      cost_rate: 0,
      description: '',
      allocations: [],
      services: [],
    });
    setEditProject(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const totalServiceHours = formData.services.reduce((sum, s) => sum + (parseFloat(s.hours) || 0), 0);
    const effectiveBudgetedHours = formData.services.length > 0 ? totalServiceHours : formData.budgeted_hours;
    const effectiveMonthlyHours = formData.project_type === 'monthly_retainer'
      ? (formData.services.length > 0 ? totalServiceHours : formData.monthly_budget_hours)
      : null;
    for (const svc of formData.services) {
      const vertical = verticals.find(v => v.id === svc.vertical_id);
      const allocatedTotal = Math.round((svc.user_allocations || []).reduce((sum, ua) => sum + (ua.hours || 0), 0) * 100) / 100;
      if (allocatedTotal > svc.hours) {
        toast.error(`${vertical?.name || 'A service'}: member hours (${allocatedTotal}h) exceed service hours (${svc.hours}h). Please fix before saving.`);
        return;
      }
    }
    const userHoursMap = {};
    for (const svc of formData.services) {
      for (const ua of (svc.user_allocations || [])) {
        if ((ua.hours || 0) > 0) {
          userHoursMap[ua.user_id] = (userHoursMap[ua.user_id] || 0) + (ua.hours || 0);
        }
      }
    }
    const derivedAllocations = Object.entries(userHoursMap).map(([userId, hours]) => ({ user_id: userId, allocated_hours: hours }));
    const cleanedServices = formData.services.map(svc => ({
      ...svc,
      user_allocations: (svc.user_allocations || []).filter(ua => (ua.hours || 0) > 0),
    }));
    const payload = { ...formData, services: cleanedServices, budgeted_hours: effectiveBudgetedHours, monthly_budget_hours: effectiveMonthlyHours, allocations: derivedAllocations };
    try {
      if (editProject) {
        const res = await api.put(`/projects/${editProject.id}`, payload);
        setProjects(prev => prev.map(p => p.id === editProject.id ? { ...p, ...res.data } : p));
        toast.success('Project updated');
      } else {
        const res = await api.post('/projects', payload);
        setProjects(prev => [res.data, ...prev]);
        toast.success('Project created');
      }
      setShowCreateDialog(false);
      resetForm();
    } catch (err) {
      toast.error(getApiError(err, 'Failed to save project'));
    }
  };

  const archiveProject = async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}`);
      toast.success('Project archived');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to archive project');
    }
  };

  const restoreProject = async (projectId) => {
    try {
      await api.post(`/projects/${projectId}/restore`);
      toast.success('Project restored');
      fetchProjects();
    } catch (err) {
      toast.error('Failed to restore project');
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await api.delete(`/projects/${projectId}/permanent`);
      toast.success('Project permanently deleted');
      setProjectToDelete(null);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to delete project');
    }
  };

  const updateProjectStatus = async (projectId, status) => {
    try {
      await api.put(`/projects/${projectId}`, { status });
      toast.success(`Project marked as ${status.replace('_', ' ')}`);
      fetchProjects();
    } catch (err) {
      toast.error('Failed to update project status');
    }
  };

  const openEditDialog = (project) => {
    setFormData({
      name: project.name,
      client_name: project.client_name || '',
      department: project.department,
      budgeted_hours: project.budgeted_hours,
      start_date: project.start_date ? project.start_date.split('T')[0] : '',
      end_date: project.end_date ? project.end_date.split('T')[0] : '',
      is_billable: project.is_billable,
      priority: project.priority || 'medium',
      billing_rate: project.billing_rate || 0,
      cost_rate: project.cost_rate || 0,
      description: project.description || '',
      project_type: project.project_type || 'fixed_price',
      monthly_budget_hours: project.monthly_budget_hours || null,
      allocations: project.allocations || [],
      services: (project.services || []).map(s => ({ ...s, hours: s.hours || 0, user_allocations: s.user_allocations || [] })),
    });
    setEditProject(project);
    setShowCreateDialog(true);
  };

  const addService = (verticalId) => {
    if (formData.services.find(s => s.vertical_id === verticalId)) return;
    const newServices = [...formData.services, { vertical_id: verticalId, work_type_ids: [], hours: 0 }];
    const newTotal = newServices.reduce((sum, s) => sum + (parseFloat(s.hours) || 0), 0);
    setFormData({ ...formData, services: newServices, budgeted_hours: newTotal, monthly_budget_hours: newTotal || formData.monthly_budget_hours });
  };

  const removeService = (verticalId) => {
    const newServices = formData.services.filter(s => s.vertical_id !== verticalId);
    const newTotal = newServices.reduce((sum, s) => sum + (parseFloat(s.hours) || 0), 0);
    setFormData({ ...formData, services: newServices, budgeted_hours: newTotal, monthly_budget_hours: newTotal || formData.monthly_budget_hours });
  };

  const toggleWorkType = (verticalId, workTypeId) => {
    setFormData({
      ...formData,
      services: formData.services.map(s => {
        if (s.vertical_id !== verticalId) return s;
        const has = s.work_type_ids.includes(workTypeId);
        return { ...s, work_type_ids: has ? s.work_type_ids.filter(id => id !== workTypeId) : [...s.work_type_ids, workTypeId] };
      }),
    });
  };

  const updateServiceHours = (verticalId, hours) => {
    const newServices = formData.services.map(s =>
      s.vertical_id === verticalId ? { ...s, hours: parseFloat(hours) || 0 } : s
    );
    const newTotal = newServices.reduce((sum, s) => sum + (parseFloat(s.hours) || 0), 0);
    setFormData({ ...formData, services: newServices, budgeted_hours: newTotal, monthly_budget_hours: newTotal || formData.monthly_budget_hours });
  };

  const addServiceUser = (verticalId, userId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => {
        if (s.vertical_id !== verticalId) return s;
        if ((s.user_allocations || []).find(ua => ua.user_id === userId)) return s;
        return { ...s, user_allocations: [...(s.user_allocations || []), { user_id: userId, hours: 0 }] };
      }),
    }));
  };

  const removeServiceUser = (verticalId, userId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => {
        if (s.vertical_id !== verticalId) return s;
        return { ...s, user_allocations: (s.user_allocations || []).filter(ua => ua.user_id !== userId) };
      }),
    }));
  };

  const updateServiceUserHours = (verticalId, userId, hours) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.map(s => {
        if (s.vertical_id !== verticalId) return s;
        return {
          ...s,
          user_allocations: (s.user_allocations || []).map(ua =>
            ua.user_id === userId ? { ...ua, hours: parseFloat(hours) || 0 } : ua
          ),
        };
      }),
    }));
  };

  const filteredProjects = projects
    .filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(search.toLowerCase()) ||
        (p.client_name || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus = (!isAdmin && !isSuperAdmin) ? p.status === 'active' : (statusFilter === 'all' || p.status === statusFilter);
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'name_asc':  return a.name.localeCompare(b.name);
        case 'name_desc': return b.name.localeCompare(a.name);
        case 'hours_asc': return (a.total_hours ?? 0) - (b.total_hours ?? 0);
        case 'hours_desc': return (b.total_hours ?? 0) - (a.total_hours ?? 0);
        case 'date_newest': return new Date(b.start_date) - new Date(a.start_date);
        case 'date_oldest': return new Date(a.start_date) - new Date(b.start_date);
        default: return 0;
      }
    });

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-orange-100 text-orange-700 border border-orange-300',
      on_hold: 'bg-gray-100 text-gray-700 border border-gray-300',
      completed: 'bg-green-100 text-green-700 border border-green-300',
      archived: 'bg-red-100 text-red-700 border border-red-300',
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${styles[status] || styles.active}`}>
        {status.replace('_', ' ')}
      </span>
    );
  };

  const getDepartmentLabel = (dept) => {
    const labels = {
      engineering: 'Development',
      development: 'Development',
      marketing: 'Marketing',
      design: 'Design',
      project_management: 'PM',
      leadership: 'Leadership',
      hr: 'HR',
    };
    return labels[dept] || dept;
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="projects-page">
      {/* Banner */}
      <div className="relative overflow-hidden rounded-2xl border border-border bg-gradient-to-br from-blue-600/10 via-indigo-500/5 to-violet-400/10 p-6">
        <div className="pointer-events-none absolute -top-10 -right-10 w-48 h-48 rounded-full bg-primary/5 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-8 -left-8 w-36 h-36 rounded-full bg-primary/5 blur-2xl" />
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-background/70 backdrop-blur-sm border border-border flex items-center justify-center shadow-sm">
              <FolderKanban className="w-7 h-7 text-primary" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="inline-block w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  {projects.filter(p => p.status === 'active').length} Active · {projects.length} Total
                </span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-primary to-violet-500 bg-clip-text text-transparent">Projects</span>
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5 flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-primary/60" />
                Manage project budgets and team allocations
              </p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex-shrink-0 flex flex-wrap items-center gap-2">
              <input ref={importFileRef} type="file" accept=".csv" className="hidden" onChange={handleProjectImport} />
              <Button variant="outline" size="sm" className="gap-1.5" onClick={downloadProjectTemplate} title="Download Excel template">
                <Download className="w-3.5 h-3.5" /> Template
              </Button>
              <Button variant="outline" size="sm" className="gap-1.5" onClick={() => importFileRef.current?.click()} disabled={importing}>
                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                {importing ? 'Importing…' : 'Import'}
              </Button>
              <Dialog open={showCreateDialog} onOpenChange={(open) => { setShowCreateDialog(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="gap-2 bg-accent text-accent-foreground hover:bg-accent/90" data-testid="create-project-btn">
                    <Plus className="w-4 h-4" /> New Project
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
              <DialogHeader>
                <DialogTitle>{editProject ? 'Edit Project' : 'Create New Project'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Project Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="project-name-input"
                    />
                  </div>
                  <div>
                    <Label>Client Name</Label>
                    <Input
                      value={formData.client_name}
                      onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                      placeholder="Optional"
                      data-testid="client-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Department</Label>
                    <Select value={formData.department} onValueChange={(v) => setFormData({ ...formData, department: v })}>
                      <SelectTrigger data-testid="department-select">
                        <SelectValue placeholder="Select Department" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="development">Development</SelectItem>
                        <SelectItem value="marketing">Marketing</SelectItem>
                        <SelectItem value="design">Design</SelectItem>
                        <SelectItem value="project_management">Project Management</SelectItem>
                        <SelectItem value="leadership">Leadership</SelectItem>
                        <SelectItem value="hr">HR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Priority</Label>
                    <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Low</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="high">High</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <>
                      <Label>Budgeted Hours</Label>
                      <Input
                        type="number"
                        min="0"
                        step="any"
                        value={formData.budgeted_hours}
                        onChange={(e) => setFormData({ ...formData, budgeted_hours: Math.max(0, parseFloat(e.target.value) || 0) })}
                        required
                        data-testid="budgeted-hours-input"
                      />
                    </>
                  </div>
                  <div>
                    <Label>Billing Rate (₹/hr)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={formData.billing_rate}
                      onChange={(e) => setFormData({ ...formData, billing_rate: Math.max(0, parseFloat(e.target.value) || 0) })}
                      disabled={!formData.is_billable}
                      className={!formData.is_billable ? 'opacity-40 cursor-not-allowed' : ''}
                    />
                  </div>
                  <div>
                    <Label>Cost Rate (₹/hr)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="any"
                      value={formData.cost_rate}
                      onChange={(e) => setFormData({ ...formData, cost_rate: Math.max(0, parseFloat(e.target.value) || 0) })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={formData.start_date}
                      onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={formData.end_date}
                      onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Project Type</Label>
                    <Select value={formData.project_type} onValueChange={(v) => setFormData({ ...formData, project_type: v, monthly_budget_hours: v === 'monthly_retainer' ? (formData.monthly_budget_hours || 160) : null })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fixed_price">Fixed Price</SelectItem>
                        <SelectItem value="monthly_retainer">Monthly Retainer</SelectItem>
                        <SelectItem value="time_and_material">Time & Material</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.project_type === 'monthly_retainer' && (
                    <div>
                      <>
                        <Label>Monthly Budget Hours</Label>
                        <Input
                          type="number"
                          step="any"
                          value={formData.monthly_budget_hours || ''}
                          onChange={(e) => setFormData({ ...formData, monthly_budget_hours: parseFloat(e.target.value) || null })}
                          placeholder="e.g. 160"
                          min="0"
                        />
                      </>
                    </div>
                  )}
                </div>

                {/* Billable toggle */}
                <div className="flex items-center justify-between px-3 py-2.5 border border-border rounded-md">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <div className="text-sm font-medium">Billable Project</div>
                      <div className="text-xs text-muted-foreground">Turn off for internal or non-billable work</div>
                    </div>
                  </div>
                  <Switch
                    checked={formData.is_billable}
                    onCheckedChange={(v) => setFormData({ ...formData, is_billable: v })}
                  />
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                  />
                </div>

                {/* Service Verticals */}
                {verticals.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Layers className="w-4 h-4 text-accent" />
                      <Label>Services (Verticals & Work Types)</Label>
                    </div>
                    <div className="mt-2 space-y-3">
                      {formData.services.map(svc => {
                        const vertical = verticals.find(v => v.id === svc.vertical_id);
                        if (!vertical) return null;
                        const verticalWorkTypes = workTypes.filter(wt => wt.vertical_id === svc.vertical_id);
                        const svcAllocatedTotal = (svc.user_allocations || []).reduce((sum, ua) => sum + (ua.hours || 0), 0);
                        const svcIsOver = (svc.user_allocations || []).length > 0 && svcAllocatedTotal > svc.hours;
                        return (
                          <div key={svc.vertical_id} className={`border rounded-md p-3 space-y-2 transition-colors ${svcIsOver ? 'border-destructive bg-destructive/5' : 'border-border'}`}>
                            {/* Service header */}
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: vertical.color || '#6366f1' }} />
                                <span className={`font-medium text-sm ${svcIsOver ? 'text-destructive' : ''}`}>{vertical.name}</span>
                                {svcIsOver && <span className="text-xs bg-destructive text-destructive-foreground px-1.5 py-0.5 rounded-full font-medium">Over limit</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex items-center gap-1.5">
                                  <Input
                                    type="number"
                                    min="0"
                                    step="any"
                                    value={svc.hours || 0}
                                    onChange={(e) => updateServiceHours(svc.vertical_id, e.target.value)}
                                    className={`w-20 h-7 text-sm text-center font-mono ${svcIsOver ? 'border-destructive text-destructive focus-visible:ring-destructive' : ''}`}
                                  />
                                  <span className="text-xs text-muted-foreground">hrs</span>
                                </div>
                                <Button type="button" variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeService(svc.vertical_id)}>
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                            {/* Work type tags */}
                            {verticalWorkTypes.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 pt-1">
                                {verticalWorkTypes.map(wt => {
                                  const active = svc.work_type_ids.includes(wt.id);
                                  return (
                                    <button
                                      type="button"
                                      key={wt.id}
                                      onClick={() => toggleWorkType(svc.vertical_id, wt.id)}
                                      className={`text-xs px-2 py-1 rounded-full border transition-colors ${active ? 'bg-accent border-accent text-white font-medium' : 'border-border text-foreground hover:border-accent/50'}`}
                                    >
                                      {wt.name}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            {/* User Allocations within this service */}
                            <div className="mt-2 pt-2 border-t border-border/50 space-y-1.5">
                              <span className="text-xs text-muted-foreground font-medium">Members</span>
                              {(svc.user_allocations || []).map(ua => {
                                const member = users.find(u => u.id === ua.user_id);
                                const hasNoHours = (ua.hours || 0) === 0;
                                return (
                                  <div key={ua.user_id} className="flex flex-col gap-0.5">
                                    <div className="flex items-center gap-2">
                                    <span className="flex-1 text-xs truncate">{member ? `${member.first_name} ${member.last_name}` : 'Unknown'}</span>
                                    <ServiceHoursInput
                                      value={ua.hours || 0}
                                      onChange={(val) => updateServiceUserHours(svc.vertical_id, ua.user_id, val)}
                                      className={`w-16 h-6 text-xs text-center font-mono ${hasNoHours ? 'border-orange-400' : ''}`}
                                    />
                                    <span className="text-xs text-muted-foreground">hrs</span>
                                    <button
                                      type="button"
                                      onClick={() => removeServiceUser(svc.vertical_id, ua.user_id)}
                                      className="text-muted-foreground hover:text-destructive"
                                    >
                                      <X className="w-3 h-3" />
                                    </button>
                                    </div>
                                    {hasNoHours && (
                                      <span className="text-xs text-orange-500 pl-0.5">⚠ 0 hrs — member won't be assigned to project</span>
                                    )}
                                  </div>
                                );
                              })}
                              {(svc.user_allocations || []).length > 0 && (() => {
                                const allocatedTotal = Math.round((svc.user_allocations || []).reduce((sum, ua) => sum + (ua.hours || 0), 0) * 100) / 100;
                                const remaining = Math.round((svc.hours - allocatedTotal) * 100) / 100;
                                const isOver = allocatedTotal > svc.hours;
                                return (
                                  <div className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${isOver ? 'bg-destructive/15 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                                    {allocatedTotal}h allocated · {isOver ? `${Math.abs(remaining)}h over limit` : `${remaining}h remaining`}
                                  </div>
                                );
                              })()}
                              <Select onValueChange={(userId) => addServiceUser(svc.vertical_id, userId)} value="">
                                <SelectTrigger className="h-7 text-xs text-muted-foreground border-dashed">
                                  <SelectValue placeholder="+ Add member" />
                                </SelectTrigger>
                                <SelectContent>
                                  {users
                                    .filter(u => !(svc.user_allocations || []).find(ua => ua.user_id === u.id))
                                    .map(u => (
                                      <SelectItem key={u.id} value={u.id}>
                                        {u.first_name} {u.last_name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        );
                      })}
                      {formData.services.length < verticals.length && (
                        <Select onValueChange={addService} value="">
                          <SelectTrigger className="border-dashed border-border hover:border-accent/60 hover:bg-accent/5 text-muted-foreground hover:text-accent transition-colors h-9 gap-2 justify-start">
                            <Plus className="w-3.5 h-3.5 flex-shrink-0" />
                            <span className="text-sm">Add service</span>
                          </SelectTrigger>
                          <SelectContent>
                            {verticals.filter(v => !formData.services.find(s => s.vertical_id === v.id)).map(v => (
                              <SelectItem key={v.id} value={v.id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: v.color || '#6366f1' }} />
                                  {v.name}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {formData.services.length > 0 && (
                        <div className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/50 text-sm">
                          <span className="text-muted-foreground">Total service hours</span>
                          <span className="font-mono font-semibold">
                            {formData.services.reduce((sum, s) => sum + (parseFloat(s.hours) || 0), 0)}h
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm border-t border-border pt-3 pb-1 -mx-1 px-1 flex justify-end gap-2 mt-4">
                  <Button type="button" variant="outline" onClick={() => { setShowCreateDialog(false); resetForm(); }}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-accent text-accent-foreground hover:bg-accent/90" data-testid="save-project-btn">
                    {editProject ? 'Update Project' : 'Create Project'}
                  </Button>
                </div>
              </form>
                </DialogContent>
                </Dialog>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="project-search"
          />
        </div>
        <div className="flex gap-2 items-center">
          {(isAdmin || isSuperAdmin) && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="flex-1 sm:w-[160px] sm:flex-none" data-testid="status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="on_hold">On Hold</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="archived">Archived</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="flex-1 sm:w-[180px] sm:flex-none" data-tour="sort-options">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name_asc">Name (A → Z)</SelectItem>
              <SelectItem value="name_desc">Name (Z → A)</SelectItem>
              <SelectItem value="hours_asc">Hours (Low → High)</SelectItem>
              <SelectItem value="hours_desc">Hours (High → Low)</SelectItem>
              <SelectItem value="date_newest">Date (Newest)</SelectItem>
              <SelectItem value="date_oldest">Date (Oldest)</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex items-center border border-border rounded-md overflow-hidden flex-shrink-0" data-tour="view-toggle">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 transition-colors ${viewMode === 'grid' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              title="Grid view"
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 transition-colors ${viewMode === 'list' ? 'bg-accent text-accent-foreground' : 'hover:bg-muted text-muted-foreground'}`}
              title="List view"
            >
              <LayoutList className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Projects — Grid / List */}
      <div data-tour="project-grid">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
            <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mb-4">
              <Search className="w-7 h-7 text-muted-foreground/50" />
            </div>
            <h3 className="text-base font-semibold text-foreground mb-1">No projects found</h3>
            <p className="text-sm text-muted-foreground max-w-xs">
              {search
                ? `No projects match "${search}". Try a different name or clear the search.`
                : 'No projects match the selected filters. Try changing the status filter.'}
            </p>
            {search && (
              <button onClick={() => setSearch('')} className="mt-4 text-sm text-primary hover:underline font-medium">
                Clear search
              </button>
            )}
          </div>
        ) : viewMode === 'grid' ? (
          <>
          {(() => {
            const totalUsed = Math.round(filteredProjects.reduce((sum, p) => {
              const ua = (p.allocations || []).find(a => a.user_id === user?.id);
              return sum + ((isAdmin || isSuperAdmin) ? getHoursUsed(p, null) : getHoursUsed(p, ua ? user?.id : null));
            }, 0) * 10) / 10;
            const totalBudget = Math.round(filteredProjects.reduce((sum, p) => {
              if (isAdmin || isSuperAdmin) return sum + (p.budgeted_hours || 0);
              const ua = (p.allocations || []).find(a => a.user_id === user?.id);
              return sum + getBudget(p, ua);
            }, 0) * 10) / 10;
            const totalRevenue = (isAdmin || isSuperAdmin)
              ? filteredProjects.filter(p => (p.billing_rate || 0) > 0).reduce((sum, p) => sum + ((p.total_logged_hours || 0) * p.billing_rate), 0)
              : 0;
            const totalOver = totalBudget > 0 && totalUsed > totalBudget;
            return (
              <div className="flex flex-wrap items-center gap-4 px-4 py-3 mb-3 rounded-lg border border-border bg-muted/50 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                    <BarChart3 className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="text-xs font-semibold text-foreground">{filteredProjects.length} Projects Total</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">Used:</span>
                  <span className={`font-mono font-bold text-sm ${totalOver ? 'text-destructive' : 'text-foreground'}`}>{totalUsed}h</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">Allocated:</span>
                  <span className="font-mono font-bold text-sm text-foreground">{totalBudget}h</span>
                </div>
                {totalBudget > 0 && (
                  <>
                    <div className="h-4 w-px bg-border" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground text-xs">Remaining:</span>
                      <span className={`font-mono font-bold text-sm ${totalOver ? 'text-destructive' : 'text-green-600'}`}>
                        {totalOver ? `-${Math.round((totalUsed - totalBudget) * 10) / 10}h` : `${Math.round((totalBudget - totalUsed) * 10) / 10}h`}
                      </span>
                    </div>
                  </>
                )}
                {totalRevenue > 0 && (
                  <>
                    <div className="h-4 w-px bg-border" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground text-xs">Revenue:</span>
                      <span className="font-mono font-bold text-sm text-foreground">₹{(Math.round(totalRevenue * 100) / 100).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project, projectIdx) => (
              <Card key={project.id} className="card-hover" data-testid={`project-card-${project.id}`} {...(projectIdx === 0 ? { 'data-tour': 'project-card-example' } : {})}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">{project.client_name || 'Internal Project'}</p>
                    </div>
                    {isAdmin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditDialog(project)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                          {(project.status === 'on_hold' || project.status === 'completed') && (
                            <DropdownMenuItem onClick={() => updateProjectStatus(project.id, 'active')} className="text-blue-600"><PlayCircle className="w-4 h-4 mr-2" /> Mark as Active</DropdownMenuItem>
                          )}
                          {project.status !== 'archived' && project.status !== 'on_hold' && (
                            <DropdownMenuItem onClick={() => updateProjectStatus(project.id, 'on_hold')} className="text-gray-600"><PauseCircle className="w-4 h-4 mr-2" /> Mark as On Hold</DropdownMenuItem>
                          )}
                          {project.status !== 'archived' && project.status !== 'completed' && (
                            <DropdownMenuItem onClick={() => updateProjectStatus(project.id, 'completed')} className="text-green-600"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Complete</DropdownMenuItem>
                          )}
                          {project.status === 'archived' ? (
                            <DropdownMenuItem onClick={() => restoreProject(project.id)} className="text-green-600"><ArchiveRestore className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={() => archiveProject(project.id)} className="text-destructive"><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setProjectToDelete(project)} className="text-destructive font-medium"><Trash2 className="w-4 h-4 mr-2" /> Delete Permanently</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    {getStatusBadge(project.status)}
                    <Badge variant="outline">{getDepartmentLabel(project.department)}</Badge>
                    {project.project_type === 'monthly_retainer' && <Badge className="bg-blue-500/20 text-blue-600">Monthly Retainer</Badge>}
                    {project.project_type === 'time_and_material' && <Badge className="bg-purple-500/20 text-purple-600">T&M</Badge>}
                    {(!project.project_type || project.project_type === 'fixed_price') && <Badge className="bg-orange-500/20 text-orange-600 border-0">Fixed Price</Badge>}
                    {project.is_billable === false && <Badge className="bg-muted text-muted-foreground border border-border">Non-Billable</Badge>}
                  </div>
                  {(() => {
                    const cardUserAlloc = (project.allocations || []).find(a => a.user_id === user?.id);
                    const cardBudget = (isAdmin || isSuperAdmin) ? getBudget(project, null) : getBudget(project, cardUserAlloc);
                    const cardHoursUsed = (isAdmin || isSuperAdmin) ? getHoursUsed(project, null) : getHoursUsed(project, cardUserAlloc ? user?.id : null);
                    const cardOverBudget = cardBudget > 0 && cardHoursUsed > cardBudget;
                    return (
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-muted-foreground">Hours Used</span>
                          <span className={`font-mono ${cardOverBudget ? 'text-destructive' : ''}`}>
                            {cardHoursUsed}h
                            {cardBudget > 0
                              ? <span className={cardOverBudget ? 'text-destructive' : 'text-muted-foreground'}> / {cardBudget}h</span>
                              : <span className="text-muted-foreground/40 text-xs"> · no budget</span>}
                            {cardOverBudget && <span className="ml-1.5 font-medium">· +{Math.round((cardHoursUsed - cardBudget) * 10) / 10}h over budget</span>}
                          </span>
                        </div>
                        <Progress
                          value={cardBudget > 0 ? Math.min((cardHoursUsed / cardBudget) * 100, 100) : 0}
                          className={`h-2 ${cardOverBudget ? '[&>div]:bg-destructive' : ''} ${cardBudget === 0 ? 'opacity-25' : ''}`}
                        />
                      </div>
                    );
                  })()}
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-1 text-muted-foreground">
                      <Calendar className="w-4 h-4" />
                      <span>{project.start_date}</span>
                    </div>
                    <button className="flex items-center gap-1 text-muted-foreground hover:text-foreground transition-colors cursor-pointer" onClick={() => setTeamMembersProject(project)} title="View team members">
                      <Users className="w-4 h-4" />
                      <span>{project.allocations?.length || 0} members</span>
                    </button>
                  </div>
                  {/* My Service Tasks — team member grid card (collapsible) */}
                  {!isAdmin && !isSuperAdmin && (() => {
                    const myServices = (project.services || []).filter(svc =>
                      (svc.user_allocations || []).some(ua => ua.user_id === user?.id)
                    );
                    if (!myServices.length) return null;
                    const isOpen = expandedServiceCards.has(project.id);
                    const toggle = () => setExpandedServiceCards(prev => {
                      const next = new Set(prev);
                      next.has(project.id) ? next.delete(project.id) : next.add(project.id);
                      return next;
                    });
                    return (
                      <div className="border-t border-border pt-2">
                        <button
                          onClick={toggle}
                          className="w-full flex items-center justify-between text-xs font-medium text-accent hover:text-accent/80 transition-colors group"
                        >
                          <span className="flex items-center gap-1.5">
                            <Layers className="w-3.5 h-3.5" />
                            My Service Tasks
                            <span className="text-[10px] font-mono bg-accent/10 border border-accent/20 px-1.5 py-0.5 rounded-full">
                              {myServices.length}
                            </span>
                          </span>
                          <span className={`text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}>
                            ▾
                          </span>
                        </button>
                        {isOpen && (
                          <div className="mt-2 flex flex-wrap items-start gap-2">
                            {myServices.map(svc => {
                              const vertical = verticals.find(v => v.id === svc.vertical_id);
                              const myAlloc = (svc.user_allocations || []).find(ua => ua.user_id === user?.id);
                              const wts = (svc.work_type_ids || []).map(id => workTypes.find(wt => wt.id === id)?.name).filter(Boolean);
                              return (
                                <div key={svc.vertical_id} className="rounded-lg border border-border bg-muted/30 px-2.5 py-2">
                                  <div className="flex items-center gap-1.5 mb-1.5">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: vertical?.color || '#6366f1' }} />
                                    <span className="text-[11px] font-semibold text-foreground">{vertical?.name || 'Service'}</span>
                                    {(myAlloc?.hours || 0) > 0 && (
                                      <span className="ml-auto text-[10px] font-mono font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-full border border-accent/20">
                                        {myAlloc.hours}h
                                      </span>
                                    )}
                                  </div>
                                  {wts.length > 0 && (
                                    <div className="flex flex-wrap gap-1">
                                      {wts.map(wt => (
                                        <span key={wt} className="text-[10px] px-1.5 py-0.5 rounded-full bg-background text-muted-foreground border border-border font-medium">
                                          {wt}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {isAdmin && project.billing_rate > 0 && (
                    <div className="pt-2 border-t border-border">
                      <div className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Est. Revenue</span>
                        <span className="font-mono font-medium">₹{((project.total_logged_hours || 0) * project.billing_rate).toLocaleString()}</span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          </>
        ) : (
          /* List View */
          <>
          {(() => {
            const totalUsed = Math.round(filteredProjects.reduce((sum, p) => {
              const ua = (p.allocations || []).find(a => a.user_id === user?.id);
              return sum + ((isAdmin || isSuperAdmin) ? getHoursUsed(p, null) : getHoursUsed(p, ua ? user?.id : null));
            }, 0) * 10) / 10;
            const totalBudget = Math.round(filteredProjects.reduce((sum, p) => {
              if (isAdmin || isSuperAdmin) return sum + (p.budgeted_hours || 0);
              const ua = (p.allocations || []).find(a => a.user_id === user?.id);
              return sum + getBudget(p, ua);
            }, 0) * 10) / 10;
            const totalRevenue = (isAdmin || isSuperAdmin)
              ? filteredProjects.filter(p => (p.billing_rate || 0) > 0).reduce((sum, p) => sum + ((p.total_logged_hours || 0) * p.billing_rate), 0)
              : 0;
            const totalOver = totalBudget > 0 && totalUsed > totalBudget;
            return (
              <div className="flex flex-wrap items-center gap-4 px-4 py-3 mb-3 rounded-lg border border-border bg-muted/50 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center">
                    <BarChart3 className="w-3.5 h-3.5 text-primary" />
                  </div>
                  <span className="text-xs font-semibold text-foreground">{filteredProjects.length} Projects Total</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">Used:</span>
                  <span className={`font-mono font-bold text-sm ${totalOver ? 'text-destructive' : 'text-foreground'}`}>{totalUsed}h</span>
                </div>
                <div className="h-4 w-px bg-border" />
                <div className="flex items-center gap-1.5">
                  <span className="text-muted-foreground text-xs">Allocated:</span>
                  <span className="font-mono font-bold text-sm text-foreground">{totalBudget}h</span>
                </div>
                {totalBudget > 0 && (
                  <>
                    <div className="h-4 w-px bg-border" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground text-xs">Remaining:</span>
                      <span className={`font-mono font-bold text-sm ${totalOver ? 'text-destructive' : 'text-green-600'}`}>
                        {totalOver ? `-${Math.round((totalUsed - totalBudget) * 10) / 10}h` : `${Math.round((totalBudget - totalUsed) * 10) / 10}h`}
                      </span>
                    </div>
                  </>
                )}
                {totalRevenue > 0 && (
                  <>
                    <div className="h-4 w-px bg-border" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-muted-foreground text-xs">Revenue:</span>
                      <span className="font-mono font-bold text-sm text-foreground">₹{(Math.round(totalRevenue * 100) / 100).toLocaleString()}</span>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          <div className="rounded-xl border border-border overflow-hidden">
            {/* Header */}
            <div className="hidden lg:grid grid-cols-[2fr_110px_190px_90px_150px_40px] gap-4 pl-5 pr-4 py-2.5 bg-muted/60 border-b border-border text-[11px] font-semibold text-muted-foreground uppercase tracking-widest">
              <span>Project</span>
              <span>Type</span>
              <span>Hours Used</span>
              <span>Members</span>
              <span className={isAdmin ? 'text-right' : ''}>{ isAdmin ? 'Revenue' : '' }</span>
              <span />
            </div>

            {filteredProjects.map((project) => {
              const statusAccent = { active: 'border-l-orange-400', completed: 'border-l-green-500', on_hold: 'border-l-gray-400', archived: 'border-l-red-400' };
              const userAlloc = (project.allocations || []).find(a => a.user_id === user?.id);
              const hoursUsed = (isAdmin || isSuperAdmin)
                ? getHoursUsed(project, null)
                : getHoursUsed(project, userAlloc ? user?.id : null);
              const displayBudget = (isAdmin || isSuperAdmin)
                ? getBudget(project, null)
                : getBudget(project, userAlloc);
              const burnPct = displayBudget > 0 ? Math.min((hoursUsed / displayBudget) * 100, 100) : 0;
              const overBudget = displayBudget > 0 && hoursUsed > displayBudget;
              const typeBadge = project.is_billable === false
                ? <Badge className="bg-muted text-muted-foreground border border-border text-xs font-medium">Non-Billable</Badge>
                : project.project_type === 'monthly_retainer'
                ? <Badge className="bg-blue-500/15 text-blue-600 border border-blue-200 text-xs font-medium">Retainer</Badge>
                : project.project_type === 'time_and_material'
                ? <Badge className="bg-purple-500/15 text-purple-600 border border-purple-200 text-xs font-medium">T&M</Badge>
                : <Badge className="bg-orange-500/15 text-orange-600 border border-orange-200 text-xs font-medium">Fixed</Badge>;
              const actionMenu = isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => openEditDialog(project)}><Edit className="w-4 h-4 mr-2" /> Edit</DropdownMenuItem>
                    {(project.status === 'on_hold' || project.status === 'completed') && (
                      <DropdownMenuItem onClick={() => updateProjectStatus(project.id, 'active')} className="text-blue-600"><PlayCircle className="w-4 h-4 mr-2" /> Mark as Active</DropdownMenuItem>
                    )}
                    {project.status !== 'archived' && project.status !== 'on_hold' && (
                      <DropdownMenuItem onClick={() => updateProjectStatus(project.id, 'on_hold')} className="text-gray-600"><PauseCircle className="w-4 h-4 mr-2" /> Mark as On Hold</DropdownMenuItem>
                    )}
                    {project.status !== 'archived' && project.status !== 'completed' && (
                      <DropdownMenuItem onClick={() => updateProjectStatus(project.id, 'completed')} className="text-green-600"><CheckCircle2 className="w-4 h-4 mr-2" /> Mark as Complete</DropdownMenuItem>
                    )}
                    {project.status === 'archived'
                      ? <DropdownMenuItem onClick={() => restoreProject(project.id)} className="text-green-600"><ArchiveRestore className="w-4 h-4 mr-2" /> Restore</DropdownMenuItem>
                      : <DropdownMenuItem onClick={() => archiveProject(project.id)} className="text-destructive"><Archive className="w-4 h-4 mr-2" /> Archive</DropdownMenuItem>}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setProjectToDelete(project)} className="text-destructive font-medium"><Trash2 className="w-4 h-4 mr-2" /> Delete Permanently</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              );

              return (
                <div
                  key={project.id}
                  className={`border-l-[3px] ${statusAccent[project.status] || 'border-l-gray-300'} pl-3 pr-4 py-3.5 hover:bg-muted/40 transition-colors border-b border-border last:border-b-0`}
                  data-testid={`project-list-row-${project.id}`}
                >
                  {/* ── Mobile layout ── */}
                  <div className="lg:hidden space-y-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-sm">{project.name}</span>
                          {getStatusBadge(project.status)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {project.client_name || 'Internal Project'} · {getDepartmentLabel(project.department)}
                        </div>
                      </div>
                      {actionMenu}
                    </div>
                    <div className="flex items-center gap-2">
                      {typeBadge}
                      <span className={`text-xs font-mono font-medium ${overBudget ? 'text-destructive' : ''}`}>{hoursUsed}h</span>
                      {displayBudget > 0
                        ? <span className="text-xs text-muted-foreground font-mono">/ {displayBudget}h</span>
                        : <span className="text-xs text-muted-foreground/40">· no budget</span>}
                      {overBudget && <span className="text-xs text-destructive font-medium">· +{Math.round((hoursUsed - displayBudget) * 10) / 10}h over budget</span>}
                    </div>
                    <Progress value={displayBudget > 0 ? burnPct : 0} className={`h-1.5 ${overBudget ? '[&>div]:bg-destructive' : ''} ${displayBudget === 0 ? 'opacity-25' : ''}`} />
                    <div className="flex items-center justify-between pt-0.5">
                      <button
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() => setTeamMembersProject(project)}
                      >
                        <Users className="w-3.5 h-3.5" />
                        <span>{project.allocations?.length || 0} members</span>
                      </button>
                      {isAdmin && project.billing_rate > 0 && (
                        <span className="text-sm font-mono font-medium">
                          ₹{((project.total_logged_hours || 0) * project.billing_rate).toLocaleString()}
                        </span>
                      )}
                    </div>
                    {/* My Service Tasks — mobile, one row per vertical */}
                    {!isAdmin && !isSuperAdmin && (() => {
                      const myServices = (project.services || []).filter(svc =>
                        (svc.user_allocations || []).some(ua => ua.user_id === user?.id)
                      );
                      if (!myServices.length) return null;
                      return (
                        <div className="space-y-1.5 pt-1">
                          {myServices.map(svc => {
                            const vertical = verticals.find(v => v.id === svc.vertical_id);
                            const myAlloc = (svc.user_allocations || []).find(ua => ua.user_id === user?.id);
                            const wts = (svc.work_type_ids || []).map(id => workTypes.find(wt => wt.id === id)?.name).filter(Boolean);
                            return (
                              <div key={svc.vertical_id} className="flex flex-wrap items-center gap-1.5">
                                <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30 font-semibold">
                                  <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: vertical?.color || '#6366f1' }} />
                                  {vertical?.name || 'Service'}
                                  {(myAlloc?.hours || 0) > 0 && <span className="font-mono opacity-75 ml-0.5">· {myAlloc.hours}h</span>}
                                </span>
                                {wts.map(wt => (
                                  <span key={wt} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium">
                                    {wt}
                                  </span>
                                ))}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>

                  {/* ── Desktop layout (grid) ── */}
                  <div className="hidden lg:grid lg:grid-cols-[2fr_110px_190px_90px_150px_40px] lg:items-center gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm truncate">{project.name}</span>
                        {getStatusBadge(project.status)}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 text-xs text-muted-foreground">
                        <span className="truncate">{project.client_name || 'Internal Project'}</span>
                        <span className="opacity-40">·</span>
                        <span>{getDepartmentLabel(project.department)}</span>
                      </div>
                    </div>
                    <div>{typeBadge}</div>
                    <div>
                      <div className="text-xs mb-1.5">
                        <span className={`font-mono font-medium ${overBudget ? 'text-destructive' : 'text-foreground'}`}>{hoursUsed}h</span>
                        {displayBudget > 0
                          ? <span className="text-muted-foreground font-mono"> / {displayBudget}h</span>
                          : <span className="text-muted-foreground/40"> · no budget</span>}
                        {overBudget && <span className="ml-1.5 text-destructive font-medium">· +{Math.round((hoursUsed - displayBudget) * 10) / 10}h over budget</span>}
                      </div>
                      <Progress value={displayBudget > 0 ? burnPct : 0} className={`h-1.5 ${overBudget ? '[&>div]:bg-destructive' : ''} ${displayBudget === 0 ? 'opacity-25' : ''}`} />
                    </div>
                    <button
                      className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
                      onClick={() => setTeamMembersProject(project)}
                    >
                      <Users className="w-3.5 h-3.5" />
                      <span className="font-medium">{project.allocations?.length || 0}</span>
                    </button>
                    {isAdmin ? (
                      <div className="text-sm font-mono font-medium text-right">
                        {project.billing_rate > 0
                          ? `₹${((project.total_logged_hours || 0) * project.billing_rate).toLocaleString()}`
                          : <span className="text-muted-foreground font-normal">—</span>}
                      </div>
                    ) : <div />}
                    {actionMenu ?? <div />}
                  </div>
                  {/* My Service Tasks — desktop sub-row, one row per vertical */}
                  {!isAdmin && !isSuperAdmin && (() => {
                    const myServices = (project.services || []).filter(svc =>
                      (svc.user_allocations || []).some(ua => ua.user_id === user?.id)
                    );
                    if (!myServices.length) return null;
                    return (
                      <div className="hidden lg:block border-t border-border/50 mt-2 pt-2 pb-0.5 space-y-1.5">
                        {myServices.map((svc, idx) => {
                          const vertical = verticals.find(v => v.id === svc.vertical_id);
                          const myAlloc = (svc.user_allocations || []).find(ua => ua.user_id === user?.id);
                          const wts = (svc.work_type_ids || []).map(id => workTypes.find(wt => wt.id === id)?.name).filter(Boolean);
                          return (
                            <div key={svc.vertical_id} className="flex flex-wrap items-center gap-1.5">
                              {idx === 0 && <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest mr-1">My Tasks:</span>}
                              {idx > 0  && <span className="text-[10px] font-semibold text-transparent uppercase tracking-widest mr-1 select-none">My Tasks:</span>}
                              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/30 font-semibold">
                                <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: vertical?.color || '#6366f1' }} />
                                {vertical?.name || 'Service'}
                                {(myAlloc?.hours || 0) > 0 && <span className="font-mono opacity-75 ml-0.5">· {myAlloc.hours}h</span>}
                              </span>
                              {wts.map(wt => (
                                <span key={wt} className="inline-flex items-center text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium">
                                  {wt}
                                </span>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Sticky Summary Bar - list view, all users */}
      {viewMode === 'list' && !loading && filteredProjects.length > 0 && (() => {
        const totalUsed = Math.round(filteredProjects.reduce((sum, p) => {
          const ua = (p.allocations || []).find(a => a.user_id === user?.id);
          return sum + ((isAdmin || isSuperAdmin) ? getHoursUsed(p, null) : getHoursUsed(p, ua ? user?.id : null));
        }, 0) * 10) / 10;
        const totalBudget = Math.round(filteredProjects.reduce((sum, p) => {
          if (isAdmin || isSuperAdmin) return sum + (p.budgeted_hours || 0);
          const ua = (p.allocations || []).find(a => a.user_id === user?.id);
          return sum + getBudget(p, ua);
        }, 0) * 10) / 10;
        const totalRevenue = (isAdmin || isSuperAdmin)
          ? filteredProjects.filter(p => (p.billing_rate || 0) > 0).reduce((sum, p) => sum + ((p.total_logged_hours || 0) * p.billing_rate), 0)
          : 0;
        const totalOver = totalBudget > 0 && totalUsed > totalBudget;
        const remaining = Math.round((totalBudget - totalUsed) * 10) / 10;
        return (
          <>
            {/* Mobile: dark bottom sheet (same as desktop pill) */}
            <div className="lg:hidden sticky bottom-0 z-40 -mx-4 text-white rounded-t-2xl shadow-lg px-5 py-3 flex flex-col gap-1" style={{ backgroundColor: 'hsl(222deg 61.55% 43.43%)' }}>
              {/* Row 1: hours */}
              <div className="flex items-center gap-1.5">
                <span className={`text-sm font-mono font-bold ${totalOver ? 'text-red-400' : ''}`}>{totalUsed}h used</span>
                {totalBudget > 0 && <span className="text-sm font-mono opacity-60">/ {totalBudget}h allocated</span>}
                {totalBudget > 0 && (
                  <span className={`text-sm font-mono font-semibold ml-1 ${totalOver ? 'text-red-400' : 'text-green-400'}`}>
                    · {totalOver ? `-${Math.round((totalUsed - totalBudget) * 10) / 10}h over` : `${remaining}h left`}
                  </span>
                )}
              </div>
              {/* Row 2: revenue */}
              {totalRevenue > 0 && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs opacity-60">Total Revenue</span>
                  <span className="text-sm font-mono font-bold">₹{(Math.round(totalRevenue * 100) / 100).toLocaleString()}</span>
                </div>
              )}
            </div>
            {/* Desktop: floating pill */}
            <div className="hidden lg:flex sticky bottom-4 z-40 justify-end pointer-events-none">
              <div className="pointer-events-auto inline-flex items-center gap-3 text-white px-5 py-3 rounded-xl shadow-lg" style={{ backgroundColor: 'hsl(222deg 61.55% 43.43%)' }}>
                <span className={`text-sm font-mono font-bold ${totalOver ? 'text-red-400' : ''}`}>{totalUsed}h used</span>
                {totalBudget > 0 && <>
                  <span className="text-sm font-mono opacity-60">/ {totalBudget}h allocated</span>
                  <div className="w-px h-4 bg-background/20" />
                  <span className={`text-sm font-mono font-semibold ${totalOver ? 'text-red-400' : 'text-green-400'}`}>
                    {totalOver ? `-${Math.round((totalUsed - totalBudget) * 10) / 10}h over` : `· ${remaining}h remaining`}
                  </span>
                </>}
                {totalRevenue > 0 && <>
                  <div className="w-px h-4 bg-background/20" />
                  <span className="text-sm font-medium opacity-60">Revenue</span>
                  <span className="text-sm font-mono font-bold">₹{(Math.round(totalRevenue * 100) / 100).toLocaleString()}</span>
                </>}
              </div>
            </div>
          </>
        );
      })()}

      {/* Team Members Dialog */}
      <Dialog open={!!teamMembersProject} onOpenChange={(open) => !open && setTeamMembersProject(null)}>
        <DialogContent className="max-w-sm sm:max-w-md">
          <DialogHeader className="pr-6">
            <DialogTitle className="flex items-start gap-2">
              <Users className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="truncate font-semibold">{teamMembersProject?.name}</div>
                <div className="text-sm font-normal text-muted-foreground">Team Members</div>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="mt-2 space-y-2">
            {(teamMembersProject?.allocations?.length || 0) === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No team members allocated.</p>
            ) : (
              teamMembersProject.allocations.map((alloc) => {
                const user = users.find(u => u.id === alloc.user_id);
                return (
                  <div key={alloc.user_id} className="flex items-center justify-between p-3 border border-border rounded-md">
                    <div>
                      <p className="text-sm font-medium">
                        {user ? `${user.first_name} ${user.last_name}` : `User #${alloc.user_id}`}
                      </p>
                      {user?.department && (
                        <p className="text-xs text-muted-foreground">{getDepartmentLabel(user.department)}</p>
                      )}
                    </div>
                    <span className="text-sm text-muted-foreground font-mono">{alloc.allocated_hours}h allocated</span>
                  </div>
                );
              })
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!projectToDelete} onOpenChange={(open) => !open && setProjectToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Project Permanently?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete <strong>{projectToDelete?.name}</strong> and all its time entries. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteProject(projectToDelete?.id)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProjectsPage;
